import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { User } from '../../interfaces/routering/user';
import { Organisation } from '../../interfaces/routering/organisation';
import { LocalStorageService } from './local-storage.service';
import { Router } from '@angular/router';
import { ServerResponse } from '../../interfaces/base/server.response';
import { ApiEndpointsService } from './api-endpoints.service';
import { HttpBackend, HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import * as Sentry from '@sentry/browser';
import * as _ from 'lodash';
import { catchError, map } from 'rxjs/operators';
import * as moment from 'moment';

@Injectable()

export class AuthenticationService {
  private _user: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  public user$: Observable<User> = this._user as Observable<User>;

  private lastUserValidation: number = null;
  private readonly VALIDATION_INTERVAL: number = 60 * 60 * 1000; // revalidate user every hour
  private validationTimer: any;

  private _organisation: BehaviorSubject<Organisation> = new BehaviorSubject<Organisation>(null);
  public organisation$: Observable<Organisation> = this._organisation as Observable<Organisation>;

  public returnUrl: string = '/';

  get currentUser(): User | null {
    return this._user.value;
  }

  constructor(private api: ApiService,
              private httpBackend: HttpBackend,
              private apiEndpoints: ApiEndpointsService,
              private router: Router,
              private localStorageService: LocalStorageService,
  ) {
    this._organisation.next(this.localStorageService.get('default-organisation'));
    this.getDefaultOrganisation();

    this.organisation$.subscribe((organisation: Organisation): void => {
      this.updateAppColors(organisation);
    });

    this.user$.subscribe((user: User): void => {
      if (user) {
        Sentry.setUser({
          id: user.id,
          username: user.name,
          email: user.email,
        });
      } else {
        Sentry.setUser(null);
      }
    });

    document.addEventListener('visibilitychange', (): void => {
      if (!document.hidden && this.currentUser && this.shouldRevalidate()) {
        this.getUserAccount().subscribe();
      }
    });
  }

  updateAppColors(organisation: Organisation = null): void {
    document.documentElement.style.setProperty('--menu-background-color',
      (organisation !== null && typeof organisation.color_menu_background !== 'undefined' && organisation.color_menu_background !== ''
          ? organisation.color_menu_background
          : '#f5f5f5'
      )
    );
    document.documentElement.style.setProperty('--menu-text-color',
      (organisation !== null && typeof organisation.color_menu_text !== 'undefined' && organisation.color_menu_text !== ''
          ? organisation.color_menu_text
          : '#12619e'
      )
    );
    document.documentElement.style.setProperty('--primary-color',
      (organisation !== null && typeof organisation.color_primary_action !== 'undefined' && organisation.color_primary_action !== ''
          ? organisation.color_primary_action
          : '#238541'
      )
    );
    document.documentElement.style.setProperty('--primary-color-text',
      (organisation !== null && typeof organisation.color_primary_action_text !== 'undefined' && organisation.color_primary_action_text !== ''
          ? organisation.color_primary_action_text
          : '#ffffff'
      )
    );
    document.documentElement.style.setProperty('--secondary-color',
      (organisation !== null && typeof organisation.color_secondary_action !== 'undefined' && organisation.color_secondary_action !== ''
          ? organisation.color_secondary_action
          : '#f8d62a'
      )
    );
    document.documentElement.style.setProperty('--secondary-color-text',
      (organisation !== null && typeof organisation.color_secondary_action_text !== 'undefined' && organisation.color_secondary_action_text !== ''
          ? organisation.color_secondary_action_text
          : '#222222'
      )
    );
  }

  getDefaultOrganisation(): void {
    const httpApi: HttpClient = new HttpClient(this.httpBackend);
    httpApi.get(environment.api_endpoint + this.apiEndpoints.get('public.default-organisation'), {
      observe: 'response',
      responseType: 'json',
    }).subscribe((response: HttpResponse<ServerResponse>): void => {
      if (typeof response.body.data !== 'undefined') {
        this.localStorageService.set('default-organisation', response.body.data);
        this._organisation.next(response.body.data);
      }
    });
  }

  login(formData: any): Observable<boolean> {
    return this.api.post(this.apiEndpoints.get('auth.login'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (typeof response.data.token !== 'undefined' && typeof response.data.user !== 'undefined') {
            this.localStorageService.set('api-token', response.data.token);
            this.localStorageService.set('user', response.data.user);
            this._user.next(response.data.user);

            this.lastUserValidation = Date.now();
            this.scheduleNextValidation();

            this.router.navigateByUrl(this.returnUrl).then((): void => {
            });

            return true;
          } else if (typeof response.data.two_factor_login !== 'undefined') {
            this.localStorageService.set('two-factor-token', response.data.token);
            this.localStorageService.set('two-factor-id', response.data.id);
            this.router.navigate(['/auth/login/two-factor-authentication']).then((): void => {
            });
          }
        }

        return false;
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  loginTwoFactorCode(formData: any): Observable<boolean> {
    formData.id = this.localStorageService.get('two-factor-id');
    formData.token = this.localStorageService.get('two-factor-token');
    return this.api.post(this.apiEndpoints.get('auth.login.2fa'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (typeof response.data.token !== 'undefined' && typeof response.data.user !== 'undefined') {
            this.localStorageService.set('api-token', response.data.token);
            this.localStorageService.set('user', response.data.user);
            this._user.next(response.data.user);
            this.localStorageService.delete('two-factor-token');
            this.localStorageService.delete('two-factor-id');

            this.lastUserValidation = Date.now();
            this.scheduleNextValidation();
            this.router.navigate(['/']).then((): void => {
            });

            return true;
          }
        }

        return false;
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  loginTwoFactorRecoveryCode(formData: any): Observable<boolean> {
    formData.id = this.localStorageService.get('two-factor-id');
    formData.token = this.localStorageService.get('two-factor-token');
    return this.api.post(this.apiEndpoints.get('auth.login.2fa'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (typeof response.data.token !== 'undefined' && typeof response.data.user !== 'undefined') {
            this.localStorageService.set('api-token', response.data.token);
            this.localStorageService.set('user', response.data.user);
            this._user.next(response.data.user);
            this.localStorageService.delete('two-factor-token');
            this.localStorageService.delete('two-factor-id');

            this.lastUserValidation = Date.now();
            this.scheduleNextValidation();
            this.router.navigate(['/']).then((): void => {
            });

            return true;
          }
        }

        return false;
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  logout(): Subscription {
    return this.api.get(this.apiEndpoints.get('auth.logout')).subscribe((): void => {
      this.silentLogout();
    });
  }

  silentLogout(): void {
    this.localStorageService.delete('api-token');
    this.localStorageService.delete('api-token-impersonate');
    this.localStorageService.delete('user');

    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    this.lastUserValidation = null;

    this._user.next(null);

    // not sure why, but setTimeout is required, otherwise it does not redirect
    setTimeout((): void => {
      this.router.navigateByUrl('/auth/login').then((): void => {
      });
    }, 0);
  }

  validateCompleteAccountToken(id: number, expires: string, signature: string): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('auth.complete-account', {':id': id, ':expires': expires, ':signature': signature}), null, true);
  }

  completeAccount(id: number, expires: string, signature: string, formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.complete-account', {':id': id, ':expires': expires, ':signature': signature}), formData);
  }

  forgotPassword(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.forgot-password'), formData);
  }

  validateResetPasswordToken(token: string, email: string): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('auth.reset-password.validate', {':token': token, ':email': email}), null, true);
  }

  resetPassword(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.reset-password'), formData);
  }

  changePassword(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.change-password'), formData);
  }

  getAccount(): Observable<ServerResponse> {
    if (!environment.production) {
      console.log(this.localStorageService.get('api-token'));
    }
    return this.api.get(this.apiEndpoints.get('auth.account'), null, true);
  }

  getUserAccount(): Observable<boolean> {
    return this.api.get(this.apiEndpoints.get('auth.account'), null, true).pipe(
      map((response: ServerResponse): boolean => {
        if (!environment.production) {
          console.log('Auth: received server response', response);
        }
        if (typeof response.data !== 'undefined') {
          if (!environment.production) {
            console.log('Auth: user is validated');
          }
          if (this.differentThanLocalAccount(response.data)) {
            this.localStorageService.set('user', response.data);
          }

          this._user.next(response.data);

          this.lastUserValidation = Date.now();
          this.scheduleNextValidation();

          return true;
        } else {
          if (!environment.production) {
            console.log('Auth: user validation failed');
          }
          return false;
        }
      }),
      catchError(() => {
        if (!environment.production) {
          console.log('Auth: user validation failed');
        }
        this.silentLogout();
        return of(false);
      })
    );
  }

  getUserFromLocalStorage(): User | null {
    return this.localStorageService.get('user');
  }

  updateAccount(formData: any): Observable<boolean> {
    return this.api.post(this.apiEndpoints.get('auth.account'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (this.differentThanLocalAccount(response.data)) {
            this.localStorageService.set('user', response.data);
            this._user.next(response.data);
          }

          this.router.navigate(['/']).then((): void => {
          });

          return true;
        } else {
          return false;
        }
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  getTwoFactorAuthEnableData(): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('auth.two-factor-auth.enable'));
  }

  confirmTwoFactorAuthEnableData(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.two-factor-auth.confirm'), formData);
  }

  getRecoveryCodes(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.two-factor-auth.recovery-codes'), formData);
  }

  regenerateRecoveryCodes(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.two-factor-auth.regenerate-recovery-codes'), formData);
  }

  disableTwoFactorAuth(formData: any): Observable<boolean> {
    return this.api.post(this.apiEndpoints.get('auth.two-factor-auth.disable'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          this.localStorageService.set('user', response.data);
          this._user.next(response.data);
          return true;
        } else {
          return false;
        }
      })
    );
  }

  differentThanLocalAccount(data: User): boolean {
    const localAccount: User = this.localStorageService.get('user');

    return (
      !localAccount ||
      !_.isEqual(
        _.omit(localAccount, ['last_access_at']),
        _.omit(data, ['last_access_at'])
      )
    );
  }

  startImpersonating(formData: any): Observable<boolean> {
    return this.api.post(this.apiEndpoints.get('auth.impersonate'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (typeof response.data.token !== 'undefined' && typeof response.data.user !== 'undefined') {
            this.localStorageService.set('api-token-impersonate', this.localStorageService.get('api-token'));
            this.localStorageService.set('api-token', response.data.token);
            this.localStorageService.set('user', response.data.user);
            this._user.next(response.data.user);

            this.router.navigate(['/']).then((): void => {
            });

            return true;
          }
        }

        return false;
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  stopImpersonating(): Observable<boolean> {
    return this.api.delete(this.apiEndpoints.get('auth.impersonate'), false).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (typeof response.data.user !== 'undefined') {
            this.localStorageService.set('api-token', this.localStorageService.get('api-token-impersonate'));
            this.localStorageService.delete('api-token-impersonate');
            this.localStorageService.set('user', response.data.user);
            this._user.next(response.data.user);

            this.router.navigate(['/routering/gebruikers']).then((): void => {
            });

            return true;
          }
        }

        return false;
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  isImpersonating(): boolean {
    return this.localStorageService.get('api-token-impersonate') !== null;
  }

  getAvailability(): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('auth.availability'), null, true);
  }

  updateAvailability(formData: any): Observable<boolean> {
    return this.api.post(this.apiEndpoints.get('auth.availability'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (this.differentThanLocalAccount(response.data)) {
            this.localStorageService.set('user', response.data);
            this._user.next(response.data);
          }

          this.router.navigate(['/']).then((): void => {
          });

          return true;
        } else {
          return false;
        }
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  scheduleNextValidation(): void {
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }

    if (!environment.production) {
      console.log('Auth: scheduling next validation at ' + moment().add(this.VALIDATION_INTERVAL / 1000, 'seconds').format('Y-MM-DD HH:mm:ss'));
    }
    this.validationTimer = setTimeout((): void => {
      if (!environment.production) {
        console.log('Auth: validation timed out, re-validate user.');
      }
      if (!environment.production) {
        console.log('Auth: this is normal login, validating user...');
      }
      this.getUserAccount().subscribe((isValid: boolean): void => {
        if (!isValid) {
          this.silentLogout();
          this.router.navigateByUrl('/').then((): void => {
          });
        }

      });
    }, this.VALIDATION_INTERVAL);
  }

  shouldRevalidate(): boolean {
    if (!this.lastUserValidation) {
      return true;
    }

    return (Date.now() - this.lastUserValidation) > this.VALIDATION_INTERVAL;
  }
}
